import { useState } from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getColumnsForResource } from '../../services'
import type { Control, UseFormWatch } from 'react-hook-form'
import type { DataAccessFormValues, Column } from '../../types'

interface SectionHideColumnsProps {
    control: Control<DataAccessFormValues>
    watch: UseFormWatch<DataAccessFormValues>
}

/**
 * Section 3 — HIDE COLUMNS (collapsed by default).
 * Fetches column list for the selected resource.
 * Renders a multi-checkbox list; shows preview of selected column labels.
 */
const SectionHideColumns = ({ control, watch }: SectionHideColumnsProps) => {
    const { t, i18n } = useTranslation('settings')
    const [open, setOpen] = useState(false)
    const keycloakName = watch('keycloak_name')

    const { data, isLoading } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.DATA_ACCESS_ASSIGNMENTS,
                'columns',
                keycloakName,
            ],
            queryFn: () => getColumnsForResource(keycloakName),
            enabled: !!keycloakName && open,
        },
        { showLoading: false, showError: false }
    )

    const columns: Column[] = data?.results ?? []

    const getLabel = (col: Column) =>
        i18n.language === 'vi' ? col.label_vi : col.label_en

    return (
        <div className="bg-white rounded-lg p-6 mb-4">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-2 w-full text-left"
            >
                <span className="text-sm">{open ? '▾' : '▸'}</span>
                <h3 className="text-base font-semibold text-gray-900">
                    {t('data-access-section-hide-columns-title')}
                </h3>
            </button>

            {open && (
                <div className="mt-4">
                    <p className="text-sm text-gray-500 mb-3">
                        {t('data-access-section-hide-columns-helper')}
                    </p>

                    {!keycloakName && (
                        <p className="text-sm text-gray-400 italic">
                            {t(
                                'data-access-hide-columns-select-resource-first'
                            )}
                        </p>
                    )}

                    {keycloakName && isLoading && (
                        <p className="text-sm text-gray-400">{t('loading')}</p>
                    )}

                    {keycloakName && !isLoading && columns.length === 0 && (
                        <p className="text-sm text-gray-400 italic">
                            {t('data-access-hide-columns-none-available')}
                        </p>
                    )}

                    {keycloakName && !isLoading && columns.length > 0 && (
                        <Controller
                            control={control}
                            name="reject_columns"
                            render={({ field }) => {
                                const selected: string[] = field.value ?? []

                                const toggle = (key: string) => {
                                    const next = selected.includes(key)
                                        ? selected.filter((k) => k !== key)
                                        : [...selected, key]
                                    field.onChange(next)
                                }

                                const selectedLabels = columns
                                    .filter((c) => selected.includes(c.key))
                                    .map(getLabel)

                                return (
                                    <div className="space-y-2">
                                        {columns.map((col) => (
                                            <label
                                                key={col.key}
                                                className="flex items-center gap-2 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selected.includes(
                                                        col.key
                                                    )}
                                                    onChange={() =>
                                                        toggle(col.key)
                                                    }
                                                    className="accent-primary"
                                                />
                                                <span className="text-sm text-gray-700">
                                                    {getLabel(col)}
                                                </span>
                                            </label>
                                        ))}

                                        {selectedLabels.length > 0 && (
                                            <p className="text-xs text-amber-600 mt-2">
                                                {t(
                                                    'data-access-hide-columns-preview'
                                                )}{' '}
                                                {selectedLabels.join(', ')}
                                            </p>
                                        )}
                                    </div>
                                )
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

export default SectionHideColumns
